/**
 * The workspace `···` ▸ **Save as template** dialog (`specs/M1-B10-shell-chrome.md` §9, q456).
 *
 * monday's entry makes one *workspace* template. This product's template store is board
 * scoped (B9, `board_templates`), and a workspace template would be a new table — so this
 * reuses B9's `templates.save` through `workspaces.saveAsTemplate` and freezes the
 * workspace's boards, one template each. A board that cannot be frozen (not a main board, or
 * no permission) comes back in `skipped` with monday's own reason and is shown here rather
 * than silently dropped. Registered as a deviation in the spec's §8.
 */
import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useToast, useOverlayDismiss } from '@elizmonday/ui';
import { useTRPCClient } from '../../lib/trpc';
import { useSidebarData } from '../shell/bootstrap';
import './boards.css';

interface SaveResult {
  saved: { boardId: string; templateId: string; name: string }[];
  skipped: { boardId: string; name: string; reason: string }[];
}

export function WorkspaceTemplateDialog({
  workspaceId,
  workspaceName,
  onClose,
}: {
  workspaceId: string;
  workspaceName: string;
  onClose(): void;
}) {
  /** P-260: Escape closes it, and focus returns to the menu row that opened it. */
  useOverlayDismiss({ onClose });

  const client = useTRPCClient();
  const toast = useToast();
  const sidebar = useSidebarData(workspaceId);
  const boards = useMemo(
    () => (sidebar.data?.boards ?? []).filter((board) => board.state !== 'deleted'),
    [sidebar.data],
  );

  const [selected, setSelected] = useState<readonly string[] | null>(null);
  const [includeItems, setIncludeItems] = useState(true);
  const [result, setResult] = useState<SaveResult | null>(null);

  /** Everything is picked until the user unpicks something — monday's own "the workspace". */
  const chosen = selected ?? boards.map((board) => String(board.id));
  const toggle = (id: string) =>
    setSelected(chosen.includes(id) ? chosen.filter((entry) => entry !== id) : [...chosen, id]);

  const save = useMutation({
    mutationFn: () =>
      client.workspaces.saveAsTemplate.mutate({
        workspaceId,
        boardIds: [...chosen],
        includeItems,
      }) as Promise<SaveResult>,
    onSuccess: (data) => {
      setResult(data);
      if (data.saved.length > 0) {
        toast(
          `${data.saved.length} ${data.saved.length === 1 ? 'template' : 'templates'} saved`,
          { kind: 'positive' },
        );
      }
      if (data.skipped.length === 0) onClose();
    },
    onError: (error: unknown) => {
      toast(error instanceof Error ? error.message : 'The templates could not be saved', {
        kind: 'negative',
      });
    },
  });

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <form
        className="ez-board-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`Save ${workspaceName} as a template`}
        data-testid="workspace-template-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          if (chosen.length > 0) save.mutate();
        }}
      >
        <h2 className="ez-board-dialog__title">Save as template</h2>
        <p className="ez-board-dialog__hint">
          Templates are per board in this product, so each board in {workspaceName} becomes its
          own template.
        </p>

        {boards.length === 0 ? (
          <p className="ez-removed__empty">This workspace has no boards to save.</p>
        ) : (
          <div className="ez-board-dialog__list">
            {boards.map((board) => (
              <label key={String(board.id)} className="ez-board-dialog__check">
                <input
                  type="checkbox"
                  data-testid={`workspace-template-board-${board.id}`}
                  checked={chosen.includes(String(board.id))}
                  onChange={() => toggle(String(board.id))}
                />
                {board.name}
              </label>
            ))}
          </div>
        )}

        <label className="ez-board-dialog__check">
          <input
            type="checkbox"
            data-testid="workspace-template-items"
            checked={includeItems}
            onChange={(event) => setIncludeItems(event.target.checked)}
          />
          Include the items
        </label>

        {result && result.skipped.length > 0 ? (
          <ul className="ez-board-dialog__error" data-testid="workspace-template-skipped">
            {result.skipped.map((row) => (
              <li key={row.boardId}>
                {row.name}: {row.reason}
              </li>
            ))}
          </ul>
        ) : null}

        <footer className="ez-board-dialog__footer">
          <button type="button" className="ez-board-dialog__cancel" onClick={onClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
          <button
            type="submit"
            className="ez-board-dialog__save"
            data-testid="workspace-template-save"
            disabled={chosen.length === 0 || save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save as template'}
          </button>
        </footer>
      </form>
    </div>
  );
}
